import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { loginSchema, type LoginInput } from '@runik/shared';
import { useAuth } from '../../lib/auth/context';
import { ApiError } from '../../lib/auth/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';

export function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginInput) => {
    setError(null);
    setSubmitting(true);
    try {
      await login(data.email, data.password);
      await navigate({ to: search.from ?? '/dashboard' });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.problem.detail ?? 'Geçersiz giriş bilgisi'
          : 'Bir hata oluştu',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label htmlFor="email">E-posta</Label>
        <Input id="email" type="email" autoComplete="email" autoFocus {...register('email')} />
        {errors.email && <p className="text-sm text-rose-600">{errors.email.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Parola</Label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
        />
        {errors.password && <p className="text-sm text-rose-600">{errors.password.message}</p>}
      </div>
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? (
          <>
            <Spinner /> Gönderiliyor…
          </>
        ) : (
          'Giriş Yap'
        )}
      </Button>
    </form>
  );
}
